import { CheckboxField } from '../Fields/CheckboxField';
import { HookedForm } from '../HookedForm';
import { CustomerInterface } from '@app/models/business';
import { useCustomerTable } from './CustomerTable.hooks';
import { Pagination } from '../Pagination/Pagination';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { CustomerTableRow } from './CustomerTableRow';
import { BaseDialog } from '../BaseDialog/BaseDialog';
import { Button } from '../Button/Button';
import { QueryObserverResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useState } from 'react';
import ScheduleCampaingIcon from '@app/assets/icons/schedule-campaign.svg?react';
import AddToListIcon from '@app/assets/icons/add-list.svg?react';
import DeleteIcon from '@app/assets/icons/bin.svg?react';
import FolderIcon from '@app/assets/icons/folder.svg?react';
import UserIcon from '@app/assets/icons/menu/user.svg?react';
import CloseIcon from '@app/assets/icons/close.svg?react';
import { SortableHeader } from '../GenericTable/SortableHeader';
import { usePermissions } from '@app/contexts/PermissionContext.tsx';

const tableHeader = [
  { label: 'CUSTOMER', field: 'name' },
  { label: 'EMAIL/PHONE NUMBER', field: 'email' },
  { label: 'Source', field: 'sourceType', sortable: false },
  { label: 'LISTS', field: 'lists', className: 'max-w-[150px]' },
  { label: 'TAGS', field: 'tags', className: 'max-w-[150px]' },
];

export const ListPill = ({ name }: { name: string }) => {
  return (
    <span className="bg-base-antiFlash py-1 px-1.5 text-xs text-base-davyGray inline-flex gap-2 items-center rounded-sm h-fit">
      <FolderIcon />
      <span>{name}</span>
    </span>
  );
};

export const TagPill = ({ name }: { name: string }) => {
  return (
    <span className="bg-base-antiFlash py-1 px-1.5 text-xs text-base-davyGray inline-flex gap-2 items-center rounded-sm h-fit truncate max-w-full">
      {name}
    </span>
  );
};

export const CustomerTable = ({
  customers,
  totalCustomers,
  handleNextPage,
  handlePrevPage,
  handleLimitChange,
  externalForm,
  currentPage,
  handleScheduleCampaing,
  handleAddToList,
  handleDeleteCustomer,
  handleSortingTable,
  handleCustomerSelection,
  refetch,
  simplified,
  showSelectionCounter = false,
  disabledActions,
}: {
  customers: CustomerInterface[];
  totalCustomers: number;
  handleNextPage: (page?: number) => void;
  handlePrevPage: (page?: number) => void;
  handleLimitChange: (limit?: number) => void;
  handleScheduleCampaing?: () => void;
  handleAddToList?: (customer?: CustomerInterface) => void;
  handleDeleteCustomer?: () => void;
  handleSortingTable?: (field: string, order: 'ASC' | 'DESC') => void;
  handleCustomerSelection: (customerId?: string) => void;
  externalForm?: UseFormReturn<FieldValues, any, undefined>;
  currentPage?: string | number;
  refetch?: () => Promise<QueryObserverResult<AxiosResponse<any, any>, Error>>;
  simplified?: boolean;
  showSelectionCounter?: boolean;
  disabledActions?: {
    addToList?: boolean;
    deleteCustomer?: boolean;
    scheduleCampaign?: boolean;
  };
}) => {
  const [activeSort, setActiveSort] = useState<{
    field: string;
    order: 'ASC' | 'DESC';
  }>();

  const {
    customerTableForm,
    deleteCustomerFn,
    deleteOpen,
    handleDeleteCustomer: handleSingleCustomerDelete,
    isPendingDelete,
  } = useCustomerTable(refetch);

  const usedForm = externalForm ?? customerTableForm;
  const selectedCustomers = Array.isArray(
    usedForm.getValues('selectedCustomers')
  )
    ? usedForm.getValues('selectedCustomers')
    : [];

  const hasSelected = selectedCustomers?.length > 0;
  const isAllSelected = customers.length === selectedCustomers?.length;
  const { can } = usePermissions()

  return (
    <>
      <div className="bg-white overflow-x-auto">
        <HookedForm
          formHook={externalForm ?? customerTableForm}
          mode="onChange"
        >
          <table className="w-full">
            <thead>
              <tr className="border-b border-base-antiFlash">
                <th className="p-4 min-w-fit w-0">
                  <CheckboxField
                    name="selectAll"
                    value="all"
                    checked={isAllSelected}
                    onCheckMark={handleCustomerSelection}
                  />
                </th>
                {!hasSelected && (
                  <>
                    {' '}
                    {tableHeader.map((item, index) => {
                      if (item.sortable ?? true) {
                        return (
                          <SortableHeader
                            key={index}
                            label={item.label}
                            field={item.field}
                            className={item.className}
                            currentSortBy={activeSort?.field}
                            currentSortOrder={activeSort?.order?.toLowerCase() as 'asc' | 'desc'}
                            onSort={(field) => {
                              const newOrder = activeSort?.field === field && activeSort.order === 'ASC' ? 'DESC' : 'ASC';
                              if (handleSortingTable) {
                                handleSortingTable(field, newOrder);
                              }
                              setActiveSort({
                                field,
                                order: newOrder,
                              });
                            }}
                          />
                        );
                      }

                      return (
                        <th className="p-4 text-xs font-medium text-left text-base-coolGray uppercase cursor-pointer transition-colors">
                          {item.label}
                        </th>
                      );
                    })}
                  </>
                )}
                {hasSelected && (
                  <th
                    colSpan={5}
                    className="text-sm text-brand-baseBlue font-medium"
                    align="left"
                  >
                    {!simplified && (
                    <div className="flex gap-4 justify-between items-center">
                      <div className="flex gap-4">
                        { can('createCampaigns') &&
                        <button
                          onClick={handleScheduleCampaing}
                          disabled={disabledActions?.scheduleCampaign}
                          className="flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <ScheduleCampaingIcon /> Schedule new campaign
                        </button>
                        }
                        { can('editLists') &&
                        <button
                          onClick={() => handleAddToList?.()}
                          disabled={disabledActions?.addToList}
                          className="flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <AddToListIcon /> Add to list(s)
                        </button>
                        }
                        { can('deleteCustomers') &&
                        <button
                          onClick={() => handleDeleteCustomer?.()}
                          disabled={disabledActions?.deleteCustomer}
                          className="flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <DeleteIcon /> Delete customer(s)
                        </button>
                        }
                      </div>
                      {showSelectionCounter && (
                        <div className="font-medium">
                          <span className="text-brand-baseBlue">{selectedCustomers.length}</span>
                          <span style={{ color: '#8B919F' }}> CUSTOMER{selectedCustomers.length !== 1 ? 'S' : ''} SELECTED</span>
                        </div>
                      )}
                    </div>
                    )}
                  </th>
                )}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {customers?.map((customer) => {
                const isSelected = (
                  Array.isArray(usedForm.getValues('selectedCustomers'))
                    ? usedForm.getValues('selectedCustomers')
                    : []
                )?.find((cus: CustomerInterface) => cus.id === customer.id);

                return (
                  <CustomerTableRow
                    key={customer.id}
                    checked={
                      Boolean(isSelected) ||
                      selectedCustomers?.includes(customer.id)
                    }
                    simplified={simplified}
                    customer={customer}
                    addToListAction={() => {
                      handleAddToList && handleAddToList(customer);
                    }}
                    deleteCustomerAction={handleSingleCustomerDelete}
                    scheduleCampaignAction={() => {
                      handleScheduleCampaing && handleScheduleCampaing();
                    }}
                    handleCheckbox={handleCustomerSelection}
                    disabledActions={disabledActions}
                  />
                );
              })}
            </tbody>
          </table>
            <Pagination
              page={Number(currentPage)}
              total={totalCustomers}
              handleLimitChange={handleLimitChange}
              handleNextPage={handleNextPage}
              handlePrevPage={handlePrevPage}
            />
        </HookedForm>
      </div>
      <BaseDialog
        size="max-w-2xl"
        open={Boolean(deleteOpen)}
        onClose={handleSingleCustomerDelete}
      >
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
          <div className="bg-base-antiFlash flex items-center p-2 rounded-lg">
            <UserIcon width={24} height={24} />
          </div>
          <div className="mr-auto ">
            <h5 className="font-bold text-lg leading-tight">Delete customer</h5>
            <p className="text-base-davyGray text-sm">
              Are you sure you want to delete this customer?
            </p>
          </div>
          <div>
            <button type="button" onClick={() => handleSingleCustomerDelete()}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="px-6 pb-4">
          <p className="text-base-davyGray text-sm">
            The following customer will be deleted from all locations in
            EchoRatings. Customers connected through the CRM system will not be
            deleted from your CRM.
          </p>

          <div className="flex items-center my-6 gap-2">
            <div className="w-6 h-6 bg-base-antiFlash flex items-center justify-center rounded-sm">
              <UserIcon
                width={15}
                height={15}
                className="[&_path]:stroke-base-coolGray"
              />
            </div>
            <span>
              {deleteOpen?.firstName} {deleteOpen?.lastName}
            </span>
          </div>

          <div className="flex items-center gap-4 justify-center">
            <Button
              kind="secondary"
              className="w-full"
              type="button"
              onClick={() => handleSingleCustomerDelete()}
            >
              Cancel
            </Button>
            <Button
              kind="danger"
              disabled={isPendingDelete || !deleteOpen}
              loading={isPendingDelete}
              className="w-full"
              type="submit"
              onClick={async () => {
                await deleteCustomerFn(deleteOpen as CustomerInterface);
              }}
            >
              Delete customer
            </Button>
          </div>
        </div>
      </BaseDialog>
    </>
  );
};
